import type { VegaLiteVisualization } from '@terra/shared-dto-chat-types';
import { applyTerraVegaLiteTheme } from './vega-lite-terra-theme';
import {
  isVegaLiteJsonObject,
  isVegaLiteVisualizationPayload,
  type VegaLiteJsonObject,
} from './vega-lite.types';

const MAX_PAYLOAD_PARSE_DEPTH = 8;
const DEFAULT_CHART_HEIGHT = 280;

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function toVegaLiteVisualization(
  record: VegaLiteJsonObject,
): VegaLiteVisualization | null {
  if (!isVegaLiteVisualizationPayload(record)) {
    return null;
  }

  return {
    type: 'vega_lite_visualization',
    title: record.title,
    description: readOptionalString(record.description),
    scopeLabel: readOptionalString(record.scopeLabel) ?? '',
    spec: record.spec,
  };
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function readPayload(value: unknown, depth = 0): VegaLiteVisualization | null {
  if (depth > MAX_PAYLOAD_PARSE_DEPTH) {
    return null;
  }

  const parsed = typeof value === 'string' ? safeJsonParse(value) : value;

  if (!isVegaLiteJsonObject(parsed)) {
    return null;
  }

  const direct = toVegaLiteVisualization(parsed);

  if (direct) {
    return direct;
  }

  const nested = parsed.output ?? parsed.result ?? parsed.content;

  if (nested && nested !== parsed) {
    return readPayload(nested, depth + 1);
  }

  return null;
}

const visualizationPayloadCache = new Map<
  string,
  VegaLiteVisualization | null
>();
const VISUALIZATION_PAYLOAD_CACHE_MAX = 64;

export function readVegaLiteVisualization(
  output: unknown,
  input: unknown,
): VegaLiteVisualization | null {
  return readPayload(output) ?? readPayload(input);
}

export function readVegaLiteVisualizationCached(
  output: unknown,
  input: unknown,
): VegaLiteVisualization | null {
  const cacheKey = `${buildVegaLitePayloadCacheKey(output)}\u0000${buildVegaLitePayloadCacheKey(input)}`;
  const cached = visualizationPayloadCache.get(cacheKey);

  if (cached !== undefined) {
    return cached;
  }

  const visualization = readVegaLiteVisualization(output, input);

  if (visualizationPayloadCache.size >= VISUALIZATION_PAYLOAD_CACHE_MAX) {
    const oldestKey = visualizationPayloadCache.keys().next().value;

    if (oldestKey) {
      visualizationPayloadCache.delete(oldestKey);
    }
  }

  visualizationPayloadCache.set(cacheKey, visualization);

  return visualization;
}

export function buildVegaLitePayloadCacheKey(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  return JSON.stringify(value);
}

export function buildResponsiveVegaLiteSpec(
  spec: VegaLiteJsonObject,
): VegaLiteJsonObject {
  const height =
    typeof spec.height === 'number' && Number.isFinite(spec.height)
      ? spec.height
      : DEFAULT_CHART_HEIGHT;

  const existingAutosize = isVegaLiteJsonObject(spec.autosize)
    ? spec.autosize
    : {};

  const themedSpec = applyTerraVegaLiteTheme(spec);

  return {
    ...themedSpec,
    width: 'container',
    height,
    autosize: {
      ...existingAutosize,
      type: 'fit',
      contains: 'padding',
    },
  };
}
